import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Close, Description, Download, FilterList } from '@mui/icons-material';
import {
  EmptyOrLoadingState,
  MoCard,
  MoLink,
  MoPageContainer,
  MoSpacing,
} from '@motivo/guanyin/src/components';
import { MoTabLink } from '@motivo/guanyin/src/components/MoTabLink';
import { Columns, MoDataTable } from '@motivo/guanyin/src/components/MoDataTable';
import useMotivoBasicGate from './useMotivoBasicGate';
import {
  MOCK_SESSIONS,
  MockSession,
  getSuperviseeById,
  getSupervisorForSession,
  getSuperviseesForSession,
} from './mock-data';

type SessionNotesModalProps = {
  open: boolean;
  onClose: () => void;
  session: MockSession | null;
};

function SessionNotesModal({ open, onClose, session }: SessionNotesModalProps) {
  if (!session?.notes) return null;

  const supervisor = getSupervisorForSession(session);
  const supervisees = getSuperviseesForSession(session);
  const notes = session.notes;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4">Session Notes</Typography>
          <Typography color="text.secondary">
            {session.date} at {session.time} • {session.duration}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <MoSpacing y={3}>
          {/* Participants */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Participants
            </Typography>
            <Typography>
              <strong>{supervisees.length > 1 ? 'Supervisees:' : 'Supervisee:'}</strong>{' '}
              {supervisees.map((s, i) => (
                <span key={s.id}>
                  {s.firstName} {s.lastName}
                  {i < supervisees.length - 1 && ', '}
                </span>
              ))}
            </Typography>
            <Typography>
              <strong>Supervisor:</strong> {supervisor?.firstName} {supervisor?.lastName}
              {supervisor?.suffix && `, ${supervisor.suffix}`}
            </Typography>
          </Box>

          {/* Topics Discussed */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Topics Discussed
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {notes.topics.map((topic, index) => (
                <Chip key={index} label={topic} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>

          {/* Supervisor Comments */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Supervisor Comments
            </Typography>
            <Typography>{notes.supervisorComments}</Typography>
          </Box>

          {/* Goals Discussed */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Goals Discussed
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {notes.goalsDiscussed.map((goal, index) => (
                <li key={index}>
                  <Typography>{goal}</Typography>
                </li>
              ))}
            </Box>
          </Box>

          {/* Action Items */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Action Items
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {notes.actionItems.map((item, index) => (
                <li key={index}>
                  <Typography>{item}</Typography>
                </li>
              ))}
            </Box>
          </Box>
        </MoSpacing>
      </DialogContent>
    </Dialog>
  );
}

const SESSION_TABS = ['Completed', 'Upcoming', 'Pending', 'Cancelled'] as const;

function DemoSessionsView() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSession, setSelectedSession] = useState<MockSession | null>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleViewNotes = (session: MockSession) => {
    setSelectedSession(session);
    setNotesModalOpen(true);
  };

  const handleCloseNotes = () => {
    setNotesModalOpen(false);
    setSelectedSession(null);
  };

  const filteredSessions = MOCK_SESSIONS.filter((session) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    // Check if any supervisee name matches the query
    return session.superviseeIds.some((id) => {
      const supervisee = getSuperviseeById(id);
      if (!supervisee) return false;
      const superviseeName = `${supervisee.firstName} ${supervisee.lastName}`.toLowerCase();
      return superviseeName.includes(query);
    });
  }).sort((a, b) => b.id.localeCompare(a.id));

  const columns: Columns<MockSession> = [
    {
      header: 'Date',
      content(session) {
        return (
          <Box>
            <Box>{session.date}</Box>
            <Box>{session.time}</Box>
          </Box>
        );
      },
    },
    {
      header: 'Supervisees',
      content(session) {
        const supervisees = getSuperviseesForSession(session);
        if (supervisees.length === 0) return null;

        return (
          <Box display="flex" flexDirection="column" gap={0.5}>
            {supervisees.map((supervisee) => (
              <Box key={supervisee.id} fontWeight="bold">
                <MoLink to={`/supervisees/${supervisee.id}`}>
                  {supervisee.firstName} {supervisee.lastName}
                </MoLink>
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      header: 'Duration',
      content(session) {
        return session.duration;
      },
    },
    {
      header: 'Type',
      content(session) {
        return <Box sx={{ textTransform: 'capitalize' }}>{session.sessionType}</Box>;
      },
    },
    {
      header: 'Supervisor',
      content(session) {
        const supervisor = getSupervisorForSession(session);
        if (!supervisor) return null;
        return (
          <MoLink to={`/supervisors/${supervisor.id}`}>
            {supervisor.firstName} {supervisor.lastName}
            {supervisor.suffix ? `, ${supervisor.suffix}` : ''}
          </MoLink>
        );
      },
    },
    {
      header: 'Notes',
      content(session) {
        if (!session.notes) {
          return <Typography color="text.secondary">—</Typography>;
        }
        return (
          <Button
            variant="text"
            size="small"
            startIcon={<Description />}
            onClick={() => handleViewNotes(session)}
            sx={{ color: 'primary.dark', fontWeight: 'bold' }}
          >
            View notes
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <MoPageContainer maxWidth="lg">
        <Typography variant="h2" mt={2} mb={1}>
          Sessions
        </Typography>

        <Box mb={3} display="flex" gap={3}>
          {SESSION_TABS.map((tab, index) => (
            <MoTabLink
              key={tab}
              isSelected={activeTab === index}
              onClick={() => setActiveTab(index)}
            >
              {tab}
            </MoTabLink>
          ))}
        </Box>

        <MoCard padding="0">
          <Box padding="2rem">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  id="search"
                  name="search"
                  placeholder="Search by supervisee name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="medium"
                  sx={{ width: 280 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  startIcon={<FilterList />}
                  sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                >
                  More filters
                </Button>
              </Box>
              <Button startIcon={<Download />} sx={{ fontWeight: 'bold' }}>
                Export
              </Button>
            </Box>
          </Box>
          <Divider />
          <Box padding="2rem" pt={0}>
            {filteredSessions.length === 0 ? (
              <EmptyOrLoadingState title="No sessions found." />
            ) : (
              <MoDataTable columns={columns} data={filteredSessions} />
            )}
          </Box>
        </MoCard>
      </MoPageContainer>
      <SessionNotesModal
        open={notesModalOpen}
        onClose={handleCloseNotes}
        session={selectedSession}
      />
    </>
  );
}

// Basic Sessions Page - shows empty state until setup is complete
// This is a frontend-only "smoke and mirrors" page for demo purposes

export default function BasicSessionsPage() {
  const { onboarding } = useMotivoBasicGate();

  if (!onboarding) {
    return <DemoSessionsView />;
  }

  return (
    <MoPageContainer maxWidth="lg">
      <Typography variant="h2" mt={2} mb={3}>
        Sessions
      </Typography>
      <MoCard>
        <Typography textAlign="center" py={8} color="text.secondary">
          Finish setting up your organization on the Dashboard to view sessions.
        </Typography>
      </MoCard>
    </MoPageContainer>
  );
}
